/**
 * SkeletonKpiTile — placeholder for KPI tile content.
 *
 * Renders three shimmer zones: caption-sized label, heading-2-sized value,
 * and micro-sized footnote. Uses only CSS custom-property references.
 *
 * @see specs/integration-plan.md Section 5.1
 */
import React from "react";
import { useShimmer } from "./useShimmer";

const SKELETON_ANIMATION = "skel 1.2s ease-in-out infinite";

const SKELETON_GRADIENT =
  "linear-gradient(90deg, var(--bg2) 0%, rgba(0,0,0,0.06) 50%, var(--bg2) 100%)";

const shimmerBackground =
  SKELETON_GRADIENT;

function shimmerStyle(
  animate: boolean,
  height: string,
  width: string,
): React.CSSProperties {
  return {
    height,
    width,
    borderRadius: "var(--radius-sm, 4px)",
    background: shimmerBackground,
    backgroundSize: "200% 100%",
    animation: animate ? SKELETON_ANIMATION : "none",
  };
}

export function SkeletonKpiTile(): React.ReactElement {
  const animate = useShimmer();

  const FS_CAPTION = "var(--fs-caption)";
  const FS_H2 = "var(--fs-h2)";
  const FS_MICRO = "var(--fs-micro)";
  return (
    <div
      data-testid="skeleton-kpi-tile"
      aria-hidden="true"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-sm, 8px)",
      }}
    >
      {/* Caption-sized label placeholder */}
      <div style={shimmerStyle(animate, FS_CAPTION, "40%")} />
      {/* Heading-2-sized value placeholder */}
      <div style={shimmerStyle(animate, FS_H2, "60%")} />
      {/* Micro-sized footnote placeholder */}
      <div style={shimmerStyle(animate, FS_MICRO, "30%")} />
    </div>
  );
}
